import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Pencil, Trash2, MessageSquare, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { canEditEntry } from '@/lib/permissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DatePicker from '@/components/DatePicker';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
    Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_LABEL = {
    mandat: 'Mandat', aps: 'APS', apd: 'APD', faisabilite: 'Faisabilité',
    contrat: 'Contrat', travaux: 'Travaux', reception: 'Réception',
    attestation: 'Attestation', cloture: 'Clôture',
};

function daysBetween(a, b) {
    const ms = new Date(b).getTime() - new Date(a).getTime();
    return Math.max(1, Math.round(ms / 86400000));
}

export default function ProjectGantt({ projectId, workspaceId, phases }) {
    const { user, profile, session } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ title: '', start_date: '', end_date: '', phase_id: '', is_milestone: false });

    // Comments state — { [taskId]: { items, loading, draft } }
    const [commentsByTask, setCommentsByTask] = useState({});
    const [commentCounts, setCommentCounts] = useState({});

    const authHeader = useCallback(() => ({
        headers: { Authorization: `Bearer ${session?.access_token}` },
    }), [session]);

    const loadCommentCounts = useCallback(async (taskIds) => {
        if (!taskIds.length) return;
        const { data } = await supabase
            .from('task_comments')
            .select('task_id')
            .in('task_id', taskIds);
        const counts = {};
        (data || []).forEach(c => { counts[c.task_id] = (counts[c.task_id] || 0) + 1; });
        setCommentCounts(counts);
    }, []);

    const load = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('project_tasks')
            .select('*')
            .eq('project_id', projectId)
            .order('start_date', { ascending: true, nullsFirst: false });
        setTasks(data || []);
        setLoading(false);
        // Load comment counts in parallel
        loadCommentCounts((data || []).map(t => t.id));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const loadTaskComments = async (taskId) => {
        setCommentsByTask(prev => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), loading: true } }));
        try {
            const r = await axios.get(`${API}/tasks/${taskId}/comments`, authHeader());
            setCommentsByTask(prev => ({
                ...prev,
                [taskId]: { items: r.data.items || [], loading: false, draft: prev[taskId]?.draft || '' },
            }));
        } catch (e) {
            setCommentsByTask(prev => ({ ...prev, [taskId]: { items: [], loading: false, draft: prev[taskId]?.draft || '' } }));
        }
    };

    const sendComment = async (taskId) => {
        const draft = (commentsByTask[taskId]?.draft || '').trim();
        if (!draft) return;
        try {
            const r = await axios.post(`${API}/tasks/${taskId}/comments`, { body: draft }, authHeader());
            setCommentsByTask(prev => ({
                ...prev,
                [taskId]: {
                    items: [...(prev[taskId]?.items || []), r.data.comment],
                    loading: false, draft: '',
                },
            }));
            setCommentCounts(c => ({ ...c, [taskId]: (c[taskId] || 0) + 1 }));
            toast.success('Commentaire ajouté');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Envoi du commentaire échoué');
        }
    };

    const deleteComment = async (taskId, commentId) => {
        try {
            await axios.delete(`${API}/tasks/${taskId}/comments/${commentId}`, authHeader());
            setCommentsByTask(prev => ({
                ...prev,
                [taskId]: {
                    ...prev[taskId],
                    items: (prev[taskId]?.items || []).filter(c => c.id !== commentId),
                },
            }));
            setCommentCounts(c => ({ ...c, [taskId]: Math.max(0, (c[taskId] || 1) - 1) }));
        } catch (e) {
            toast.error('Suppression refusée');
        }
    };

    const { startDate, endDate, totalDays } = useMemo(() => {
        const allDates = tasks.flatMap(t => [t.start_date, t.end_date]).filter(Boolean);
        if (allDates.length === 0) {
            const s = new Date();
            const e = new Date(); e.setMonth(e.getMonth() + 6);
            return { startDate: s.toISOString().slice(0, 10), endDate: e.toISOString().slice(0, 10), totalDays: daysBetween(s, e) };
        }
        const s = allDates.reduce((a, b) => (a < b ? a : b));
        const e = allDates.reduce((a, b) => (a > b ? a : b));
        return { startDate: s, endDate: e, totalDays: daysBetween(s, e) };
    }, [tasks]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title) return;
        if (editingId) {
            const { error } = await supabase.from('project_tasks').update({
                title: form.title,
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                phase_id: form.phase_id || null,
                is_milestone: form.is_milestone,
            }).eq('id', editingId);
            if (error) {
                toast.error("Vous n'êtes pas autorisé·e à modifier cette tâche.");
                return;
            }
            await supabase.from('audit_logs').insert({
                workspace_id: workspaceId, user_id: user?.id, action: 'task.updated',
                entity_type: 'project_task', entity_id: editingId, new_value: { title: form.title },
            });
            toast.success('Tâche modifiée');
            setOpen(false); setEditingId(null);
            setForm({ title: '', start_date: '', end_date: '', phase_id: '', is_milestone: false });
            load();
            return;
        }
        const payload = {
            workspace_id: workspaceId, project_id: projectId,
            title: form.title, start_date: form.start_date || null,
            end_date: form.end_date || null,
            phase_id: form.phase_id || null,
            is_milestone: form.is_milestone, assignee_id: user?.id,
            created_by: user?.id,
        };
        const { error } = await supabase.from('project_tasks').insert(payload);
        if (!error) {
            await supabase.from('audit_logs').insert({
                workspace_id: workspaceId, user_id: user?.id, action: 'task.created',
                entity_type: 'project_task', new_value: { title: payload.title },
            });
            toast.success('Tâche créée');
            setOpen(false);
            setForm({ title: '', start_date: '', end_date: '', phase_id: '', is_milestone: false });
            load();
        }
    };

    const openEdit = (t) => {
        setEditingId(t.id);
        setForm({
            title: t.title || '',
            start_date: t.start_date || '',
            end_date: t.end_date || '',
            phase_id: t.phase_id || '',
            is_milestone: !!t.is_milestone,
        });
        setOpen(true);
    };

    const remove = async (t) => {
        if (!window.confirm(`Supprimer la tâche « ${t.title} » ? Cette action est irréversible.`)) return;
        const { error } = await supabase.from('project_tasks').delete().eq('id', t.id);
        if (error) {
            toast.error("Vous n'êtes pas autorisé·e à supprimer cette tâche.");
            return;
        }
        await supabase.from('audit_logs').insert({
            workspace_id: workspaceId, user_id: user?.id, action: 'task.deleted',
            entity_type: 'project_task', entity_id: t.id, old_value: { title: t.title },
        });
        toast.success('Tâche supprimée');
        load();
    };

    if (loading) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…</div>;

    const CommentsButton = ({ task }) => (
        <Popover onOpenChange={(v) => { if (v) loadTaskComments(task.id); }}>
            <PopoverTrigger asChild>
                <Button
                    size="sm" variant="ghost"
                    data-testid={`gantt-task-comments-${task.id}`}
                    className="relative"
                    title="Commentaires"
                >
                    <MessageSquare size={13} />
                    {commentCounts[task.id] > 0 && (
                        <span className="absolute -top-1 -right-1 h-3.5 w-3.5 text-[9px] font-semibold rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                            {commentCounts[task.id]}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0" data-testid={`gantt-comments-popover-${task.id}`}>
                <div className="px-3 py-2.5 border-b border-border text-xs font-medium text-ink">
                    Commentaires · {task.title}
                </div>
                <div className="max-h-64 overflow-y-auto p-3 space-y-2 bg-surface-muted/30">
                    {commentsByTask[task.id]?.loading && (
                        <div className="text-xs text-muted-foreground text-center py-3">
                            <Loader2 size={12} className="inline animate-spin mr-1" /> Chargement…
                        </div>
                    )}
                    {!commentsByTask[task.id]?.loading && (commentsByTask[task.id]?.items || []).length === 0 && (
                        <div className="text-xs text-muted-foreground text-center py-3">Aucun commentaire.</div>
                    )}
                    {(commentsByTask[task.id]?.items || []).map(c => (
                        <div key={c.id} className="p-2.5 rounded-md bg-white border border-border/70 group" data-testid={`comment-${c.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="text-[11px] font-medium text-ink">{c.author_name}</div>
                                {c.user_id === user?.id && (
                                    <button
                                        type="button"
                                        onClick={() => deleteComment(task.id, c.id)}
                                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                                        data-testid={`comment-delete-${c.id}`}
                                    >
                                        <Trash2 size={11} />
                                    </button>
                                )}
                            </div>
                            <div className="text-xs text-foreground/80 whitespace-pre-wrap mt-1">{c.body}</div>
                            <div className="text-[10px] text-muted-foreground/70 mt-1">
                                {new Date(c.created_at).toLocaleString('fr-FR')}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="p-2 border-t border-border flex gap-2">
                    <Input
                        value={commentsByTask[task.id]?.draft || ''}
                        onChange={(e) => setCommentsByTask(prev => ({
                            ...prev,
                            [task.id]: { ...(prev[task.id] || { items: [], loading: false }), draft: e.target.value },
                        }))}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(task.id); } }}
                        placeholder="Ajouter un commentaire…"
                        className="h-8 text-xs"
                        data-testid={`comment-input-${task.id}`}
                    />
                    <Button
                        size="sm"
                        onClick={() => sendComment(task.id)}
                        disabled={!(commentsByTask[task.id]?.draft || '').trim()}
                        data-testid={`comment-send-${task.id}`}
                        className="h-8 px-3"
                    >
                        <Send size={12} />
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                    {tasks.length === 0 ? 'Aucune tâche. Créez votre première tâche pour démarrer la timeline.' : `${tasks.length} tâches · ${totalDays}j`}
                </div>
                <Button onClick={() => setOpen(true)} size="sm" className="bg-primary hover:bg-primary-hover" data-testid="gantt-add-task">
                    <Plus size={14} className="mr-1.5" /> Tâche
                </Button>
            </div>

            <div className="card-soft p-6 overflow-x-auto" data-testid="gantt-canvas">
                <div className="min-w-[720px] space-y-1.5">
                    {/* Timeline header */}
                    <div className="grid grid-cols-12 text-xs uppercase tracking-wider text-muted-foreground border-b border-border pb-2 mb-3">
                        <div className="col-span-4">Tâche</div>
                        <div className="col-span-8 grid grid-cols-6">
                            {[0, 1, 2, 3, 4, 5].map((i) => {
                                const d = new Date(startDate);
                                d.setDate(d.getDate() + Math.floor((totalDays / 6) * i));
                                return <div key={i}>{d.toLocaleDateString('fr-FR', { month: 'short', day: '2-digit' })}</div>;
                            })}
                        </div>
                    </div>

                    {tasks.length === 0 && (
                        <div className="text-center text-foreground/50 py-8">Aucune tâche planifiée.</div>
                    )}

                    {tasks.map((t) => {
                        const phase = phases.find(p => p.id === t.phase_id);
                        const editable = canEditEntry({ entry: t, userId: user?.id, profile, currentWorkspace });
                        if (!t.start_date || !t.end_date) {
                            return (
                                <div key={t.id} className="grid grid-cols-12 items-center py-2 text-sm group" data-testid={`gantt-task-${t.id}`}>
                                    <div className="col-span-4 truncate flex items-center gap-2">
                                        {t.is_milestone && <span className="h-2 w-2 rotate-45 bg-primary" />}
                                        <span className="font-medium">{t.title}</span>
                                    </div>
                                    <div className="col-span-6 text-xs text-muted-foreground">Dates non renseignées</div>
                                    <div className="col-span-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <CommentsButton task={t} />
                                        {editable && (
                                            <>
                                                <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`gantt-task-edit-${t.id}`}><Pencil size={13} /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`gantt-task-delete-${t.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        }
                        const offset = ((new Date(t.start_date) - new Date(startDate)) / 86400000) / totalDays * 100;
                        const width = (daysBetween(t.start_date, t.end_date) / totalDays) * 100;
                        return (
                            <div key={t.id} className="grid grid-cols-12 items-center py-1.5 text-sm group hover:bg-surface-muted -mx-3 px-3 rounded" data-testid={`gantt-task-${t.id}`}>
                                <div className="col-span-4 truncate flex items-center gap-2">
                                    {t.is_milestone && <span className="h-2 w-2 rotate-45 bg-primary" />}
                                    <span className="font-medium truncate">{t.title}</span>
                                    {phase && <span className="phase-badge bg-secondary-light text-secondary">{PHASE_LABEL[phase.phase] || phase.title || phase.phase}</span>}
                                </div>
                                <div className="col-span-6 relative h-6">
                                    <div className="absolute inset-y-0 left-0 right-0 rounded-full bg-surface-muted opacity-60" />
                                    <div
                                        className="absolute inset-y-0 rounded-full bg-primary shadow-soft"
                                        style={{ left: `${Math.max(0, offset)}%`, width: `${Math.min(100, width)}%` }}
                                    />
                                </div>
                                <div className="col-span-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <CommentsButton task={t} />
                                    {editable && (
                                        <>
                                            <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`gantt-task-edit-${t.id}`}><Pencil size={13} /></Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`gantt-task-delete-${t.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm({ title: '', start_date: '', end_date: '', phase_id: '', is_milestone: false }); } }}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{editingId ? 'Modifier la tâche' : 'Nouvelle tâche'}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2"><Label>Titre</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Début</Label><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="task-start" /></div>
                            <div className="space-y-2"><Label>Fin</Label><DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="task-end" /></div>
                        </div>
                        <div className="space-y-2">
                            <Label>Phase</Label>
                            <select value={form.phase_id} onChange={(e) => setForm({ ...form, phase_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="task-phase">
                                <option value="">—</option>
                                {phases.map(p => <option key={p.id} value={p.id}>{PHASE_LABEL[p.phase] || p.title || p.phase} — {p.title}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.is_milestone} onChange={(e) => setForm({ ...form, is_milestone: e.target.checked })} data-testid="task-milestone" /> Jalon
                        </label>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
                            <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="task-submit">Enregistrer</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
